import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { VariantEditor, VariantType } from '../components/VariantEditor.tsx';
import { addProduct } from '../utils/productApi';
import { useAuth } from '../contexts/AuthContext';
import { uploadProductImage, validateImageFile, uploadProductVideo, uploadProductCoverPhoto } from '../utils/imageUpload';
import { BackButton } from '../components/ui';

const BROWN = 'bg-[#C97B53]';
const BROWN_TEXT = 'text-[#C97B53]';
const NEUTRAL_BG = 'bg-neutral-50';
const NEUTRAL_WHITE = 'bg-white';
const ACCENT_GRAY = 'text-neutral-500';
const headingFont = 'font-sora font-bold';
const bodyFont = 'font-montserrat';
const DRAFT_KEY = 'add-product-draft-v1';

// Zod schema for form validation
const addProductSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  description: z.string().optional(),
  category: z.string().min(1, 'Category is required'),
  price: z.string().refine((val) => {
    const num = Number(val);
    return !isNaN(num) && num > 0;
  }, 'Valid price required'),
  cost: z.string().optional(),
  inventory: z.string().refine((val) => {
    const num = Number(val);
    return !isNaN(num) && num >= 0;
  }, 'Valid inventory required'),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  availableDays: z.array(z.string()).default([]),
  dailyInventory: z.record(z.string(), z.number().min(0)).default({}),
  status: z.boolean().default(true),
  ingredients: z.array(z.string()).default([]),
  ingredientInput: z.string().optional(),
  image: z.any().optional(),
  imageUrl: z.string().optional(),
});

// TypeScript type would be: type AddProductFormData = z.infer<typeof addProductSchema>;

const mockCategories = [
  'Baked Goods',
  'Ready-to-Eat',
  'Vegan',
  'Desserts',
  'Drinks',
  'Snacks',
  'Meals',
];

const daysOfWeek = [
  { label: 'M', value: 'Mon' },
  { label: 'T', value: 'Tue' },
  { label: 'W', value: 'Wed' },
  { label: 'T', value: 'Thu' },
  { label: 'F', value: 'Fri' },
  { label: 'S', value: 'Sat' },
  { label: 'S', value: 'Sun' },
];

function calcProfitMargin(price, cost) {
  if (!price || !cost || isNaN(price) || isNaN(cost) || Number(price) === 0) return '';
  return Math.max(0, (((Number(price) - Number(cost)) / Number(price)) * 100).toFixed(1));
}

export function AddProduct() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Redirect to create-product-video page
  useEffect(() => {
    navigate('/create-product-video', { replace: true });
  }, [navigate]);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(addProductSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      price: '',
      cost: '',
      inventory: '',
      startDate: '',
      endDate: '',
      availableDays: [],
      dailyInventory: {},
      status: true,
      ingredients: [],
      ingredientInput: '',
      image: null,
      imageUrl: '',
    },
  });

  // Watch form values for real-time updates
  const watchedValues = watch();
  
  const fileInputRef = useRef();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [variants, setVariants] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
  const [scrubTime, setScrubTime] = useState(0);
  const videoPlayerRef = useRef();
  const canvasRef = useRef();

  // Load categories (mock API)
  useEffect(() => {
    setCategories(mockCategories);
  }, []);

  // Draft save/restore
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      const parsed = JSON.parse(draft);
      reset(parsed);
      if (parsed.variants) setVariants(parsed.variants);
    }
  }, [reset]);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...watchedValues, variants }));
  }, [watchedValues, variants]);

  // Validation is now handled by React Hook Form + Zod

  // Handlers
  const handleInput = e => {
    const { name, value, type, checked } = e.target;
    setValue(name, type === 'checkbox' ? checked : value);
  };
  const handleImage = e => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateImageFile(file);
        setValue('image', file);
        setValue('imageUrl', URL.createObjectURL(file));
        setError('');
      } catch (error) {
        setError(error.message);
      }
    }
  };
  const handleCategory = e => {
    setValue('category', e.target.value);
  };
  const handleIngredientInput = e => {
    setValue('ingredientInput', e.target.value);
  };
  const handleAddIngredient = e => {
    e.preventDefault();
    const val = watchedValues.ingredientInput.trim();
    if (val && !watchedValues.ingredients.includes(val)) {
      setValue('ingredients', [...watchedValues.ingredients, val]);
      setValue('ingredientInput', ''); // Clear the input
    }
  };
  const handleRemoveIngredient = ing => {
    setValue('ingredients', watchedValues.ingredients.filter(i => i !== ing));
  };
  const handleDayToggle = day => {
    const currentAvailableDays = watchedValues.availableDays;
    const newAvailableDays = currentAvailableDays.includes(day)
      ? currentAvailableDays.filter(d => d !== day)
      : [...currentAvailableDays, day];
    
    const newDailyInventory = { ...watchedValues.dailyInventory };
    if (!newAvailableDays.includes(day)) {
      // If removing a day, also remove its inventory
      delete newDailyInventory[day];
    } else {
      // If adding a day, initialize it with 0 inventory
      newDailyInventory[day] = 0;
    }
    
    setValue('availableDays', newAvailableDays);
    setValue('dailyInventory', newDailyInventory);
  };

  const handleDailyInventoryChange = (day, value) => {
    const newDailyInventory = {
      ...watchedValues.dailyInventory,
      [day]: Math.max(0, parseInt(value) || 0)
    };
    setValue('dailyInventory', newDailyInventory);
  };
  const handleStatus = () => {
    setValue('status', !watchedValues.status);
  };
  const handleCancel = () => {
    if (JSON.stringify(watchedValues) !== JSON.stringify({
      name: '', description: '', category: '', price: '', cost: '', inventory: '', startDate: '', endDate: '', availableDays: [], dailyInventory: {}, status: true,
    })) {
      if (!window.confirm('Discard changes?')) return;
    }
    localStorage.removeItem(DRAFT_KEY);
    navigate(-1);
  };
  const handleVideo = async e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['video/mp4', 'video/quicktime'].includes(file.type)) {
      setError('Only MP4 and MOV files are allowed.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Video must be 50MB or less.');
      return;
    }
    setVideo(file);
    setVideoUrl(URL.createObjectURL(file));
    setValue('image', null);
    setValue('imageUrl', '');
    setError('');
  };

  // After video loads, check duration
  const handleLoadedMetadata = () => {
    const duration = videoPlayerRef.current?.duration || 0;
    setVideoDuration(duration);
    if (duration > 30) {
      setError('Video must be 30 seconds or less.');
      setVideo(null);
      setVideoUrl('');
    }
  };

  // Scrubber handler
  const handleScrub = e => {
    const time = parseFloat(e.target.value);
    setScrubTime(time);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime = time;
    }
  };

  // Capture frame as cover photo
  const handleCaptureFrame = async () => {
    if (!videoPlayerRef.current) return;
    const videoEl = videoPlayerRef.current;
    const canvas = canvasRef.current;
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async blob => {
      if (blob) {
        setCoverPhoto(blob);
        setCoverPhotoUrl(URL.createObjectURL(blob));
        // Upload to storage
        const uploadResult = await uploadProductCoverPhoto(blob, currentUser.uid, 'temp');
        setCoverPhotoUrl(uploadResult.url);
      }
    }, 'image/webp', 0.92);
  };

  const handleSubmitForm = async e => {
    e.preventDefault();
    // Additional validation for media
    if (!watchedValues.image && !video) {
      setError('Product media is required.');
      return;
    }
    if (video && !coverPhotoUrl) {
      setError('Please select a cover image from the video.');
      return;
    }
    
    const result = await handleSubmit(onSubmit)();
    if (result && result.success) {
      setSuccess(true);
      localStorage.removeItem(DRAFT_KEY);
      setTimeout(() => navigate('/seller-hub'), 1200);
    } else {
      console.log('Form submission failed:', result);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    try {
      // Upload image to Firebase Storage if provided
      let imageUrl = data.imageUrl;
      if (data.image) {
        const uploadResult = await uploadProductImage(data.image, currentUser.uid, 'temp');
        imageUrl = uploadResult.url;
      }
      // Upload video if provided
      let uploadedVideoUrl = data.videoUrl;
      if (video) {
        const uploadResult = await uploadProductVideo(video, currentUser.uid, 'temp');
        uploadedVideoUrl = uploadResult.url;
      }
      // Upload cover photo if captured
      let uploadedCoverPhotoUrl = data.coverPhotoUrl;
      if (coverPhoto && !data.coverPhotoUrl.startsWith('http')) {
        const uploadResult = await uploadProductCoverPhoto(coverPhoto, currentUser.uid, 'temp');
        uploadedCoverPhotoUrl = uploadResult.url;
      }
      // Remove non-serializable fields
      const { image, ...rest } = data;
      const productData = {
        ...rest,
        imageUrl: uploadedCoverPhotoUrl || imageUrl,
        videoUrl: uploadedVideoUrl,
        variants,
        sellerId: currentUser.uid, // Include seller ID
        dailyInventory: data.dailyInventory, // Include daily inventory data
        createdAt: new Date().toISOString(),
      };
      await addProduct(productData);
      return { success: true };
    } catch (err) {
      setError('Failed to add product. Try again.');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // UI
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center font-montserrat">
      <div className="max-w-2xl w-full">
        <form className="p-10 bg-white rounded-xl shadow-md space-y-10" aria-label="Add New Product Form" onSubmit={handleSubmitForm}>
          {/* Header */}
          <header className="flex items-center justify-between mb-6">
            <BackButton 
              onClick={handleCancel} 
              variant="standard"
              ariaLabel="Back"
            />
            <h1 className={`${headingFont} text-xl text-neutral-900`}>Add New Product</h1>
            <button aria-label="Help or Notifications" className={`${BROWN_TEXT} text-lg`}>?</button>
          </header>

          {/* Basic Information Section */}
          <section aria-labelledby="basic-info-heading" className="space-y-6 border-b border-neutral-200 pb-8">
            <h2 id="basic-info-heading" className={`${headingFont} text-lg mb-2`}>Basic Information</h2>
            {/* Media upload */}
            <div className="w-full mb-2">
              <label className="block font-semibold mb-1" htmlFor="media">Product Media *</label>
              <div className="w-full flex flex-col">
                <button
                  type="button"
                  onClick={() => !videoUrl && fileInputRef.current.click()}
                  className={`mx-auto w-full max-w-xs h-32 rounded-lg border-2 border-dashed flex items-center justify-center ${watchedValues.imageUrl || videoUrl ? 'border-transparent' : 'border-neutral-300'} bg-neutral-100 focus:outline focus:ring-2 focus:ring-brown-700`}
                  aria-label="Upload product media"
                  disabled={!!videoUrl}
                >
                  {videoUrl ? (
                    <video ref={videoPlayerRef} src={videoUrl} className="w-full h-full object-cover rounded-lg" controls onLoadedMetadata={handleLoadedMetadata} />
                  ) : watchedValues.imageUrl ? (
                    <img src={watchedValues.imageUrl} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <span className="text-4xl text-neutral-400">+</span>
                  )}
                </button>
                {/* Only show file input if no video is present */}
                {!videoUrl && (
                  <input
                    ref={fileInputRef}
                    id="media"
                    name="media"
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.type.startsWith('video/')) {
                        if (file.size > 50 * 1024 * 1024) {
                          setError('Video must be under 50MB.');
                          return;
                        }
                        setVideo(file);
                        setVideoUrl(URL.createObjectURL(file));
                        setValue('image', null);
                        setValue('imageUrl', '');
                        setError('');
                      } else if (file.type.startsWith('image/')) {
                        if (file.size > 5 * 1024 * 1024) {
                          setError('Image must be under 5MB.');
                          return;
                        }
                        setValue('image', file);
                        setValue('imageUrl', URL.createObjectURL(file));
                        setVideo(null);
                        setVideoUrl('');
                        setError('');
                      } else {
                        setError('Only image or video files are allowed.');
                        return;
                      }
                    }}
                  />
                )}
                                 {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
              </div>
              {/* Video scrubber and cover photo selection */}
              {videoUrl && videoDuration > 0 && (
                <div className="mb-4 mt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={videoDuration}
                      step={0.01}
                      value={scrubTime}
                      onChange={handleScrub}
                      onMouseUp={handleCaptureFrame}
                      onTouchEnd={handleCaptureFrame}
                      className="w-full"
                    />
                    <span className="text-xs">{scrubTime.toFixed(2)}s</span>
                  </div>
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  {coverPhotoUrl && (
                    <div className="mt-2">
                      <div className="font-semibold mb-1">Selected Cover Photo:</div>
                      <img src={coverPhotoUrl} alt="Cover" width={160} className="rounded shadow" />
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Name */}
            <div>
              <label htmlFor="name" className="block font-semibold mb-1">Product Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('name')}
                aria-required="true"
                aria-invalid={!!(errors.name)}
              />
              {errors.name && <span className="text-red-500 text-sm">{errors.name.message}</span>}
            </div>
            {/* Description */}
            <div>
              <label htmlFor="description" className="block font-semibold mb-1">Description</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('description')}
                aria-required="false"
              />
            </div>
            {/* Category */}
            <div>
              <label htmlFor="category" className="block font-semibold mb-1">Category *</label>
              <select
                id="category"
                name="category"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('category')}
                aria-required="true"
                aria-invalid={!!(errors.category)}
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.category && <span className="text-red-500 text-sm">{errors.category.message}</span>}
            </div>
          </section>

          {/* Ingredients Section */}
          <section aria-labelledby="ingredients-heading" className="space-y-3 border-b border-neutral-200 pb-8">
            <h2 id="ingredients-heading" className={`${headingFont} text-lg mb-2`}>Ingredients</h2>
            <form className="flex gap-2 mb-2" onSubmit={handleAddIngredient}>
              <input
                type="text"
                value={watchedValues.ingredientInput}
                onChange={handleIngredientInput}
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                placeholder="Add ingredient"
                aria-label="Add ingredient"
              />
              <button type="submit" className={`px-4 py-2 rounded-lg ${BROWN} text-white font-semibold`} disabled={!watchedValues.ingredientInput.trim()}>Add</button>
            </form>
            <div className="flex flex-wrap gap-2">
              {watchedValues.ingredients.map(ing => (
                <span key={ing} className="bg-neutral-200 rounded-full px-3 py-1 flex items-center text-sm">
                  {ing}
                  <button type="button" aria-label={`Remove ${ing}`} className="ml-2 text-red-500" onClick={() => handleRemoveIngredient(ing)}>&times;</button>
                </span>
              ))}
            </div>
          </section>

          {/* Variants Section */}
          <section aria-labelledby="variants-heading" className="space-y-3 border-b border-neutral-200 pb-8">
            <h2 id="variants-heading" className={`${headingFont} text-lg mb-2`}>Variants</h2>
            <VariantEditor value={variants} onChange={setVariants} />
          </section>

          {/* Pricing & Inventory Section */}
          <section aria-labelledby="pricing-heading" className="space-y-6 border-b border-neutral-200 pb-8">
            <h2 id="pricing-heading" className={`${headingFont} text-lg mb-2`}>Pricing & Inventory</h2>
            {/* Price */}
            <div>
              <label htmlFor="price" className="block font-semibold mb-1">Price ($) *</label>
              <input
                id="price"
                name="price"
                type="number"
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('price')}
                aria-required="true"
                aria-invalid={!!(errors.price)}
              />
              {errors.price && <span className="text-red-500 text-sm">{errors.price.message}</span>}
            </div>
            {/* Cost */}
            <div>
              <label htmlFor="cost" className="block font-semibold mb-1">Cost per item ($)</label>
              <input
                id="cost"
                name="cost"
                type="number"
                min="0"
                step="0.01"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('cost')}
                aria-required="false"
              />
            </div>
            {/* Profit Margin */}
            <div>
              <label className="block font-semibold mb-1">Profit Margin (%)</label>
              <div className="w-full rounded-lg border border-neutral-200 px-3 py-2 bg-neutral-100 text-neutral-700">
                {calcProfitMargin(watchedValues.price, watchedValues.cost) || '--'}
              </div>
            </div>
            {/* Inventory */}
            <div>
              <label htmlFor="inventory" className="block font-semibold mb-1">Inventory *</label>
              <input
                id="inventory"
                name="inventory"
                type="number"
                min="0"
                step="1"
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                {...register('inventory')}
                aria-required="true"
                aria-invalid={!!(errors.inventory)}
              />
              {errors.inventory && <span className="text-red-500 text-sm">{errors.inventory.message}</span>}
            </div>
          </section>

          {/* Availability Section */}
          <section aria-labelledby="availability-heading" className="space-y-6">
            <h2 id="availability-heading" className={`${headingFont} text-lg mb-2`}>Availability</h2>
            <div className="block w-full">
              {/* Date row: side by side on sm+, stacked on mobile */}
              <div className="w-full flex flex-col sm:flex-row gap-4 mb-4">
                <div className="w-full sm:w-1/2">
                  <label htmlFor="startDate" className="block font-semibold mb-1">Start Date</label>
                  <input
                    id="startDate"
                    name="startDate"
                    type="date"
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                    {...register('startDate')}
                  />
                </div>
                <div className="w-full sm:w-1/2 mt-4 sm:mt-0">
                  <label htmlFor="endDate" className="block font-semibold mb-1">End Date</label>
                  <input
                    id="endDate"
                    name="endDate"
                    type="date"
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 focus:outline focus:ring-2 focus:ring-brown-700"
                    {...register('endDate')}
                  />
                </div>
              </div>
              {/* Available Days (always stacked below date row) */}
              <div className="block w-full mb-4">
                <label className="block font-semibold mb-1">Available Days</label>
                <div className="flex gap-2 flex-wrap">
                  {daysOfWeek.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      aria-pressed={watchedValues.availableDays.includes(day.value)}
                      onClick={() => handleDayToggle(day.value)}
                      className={`px-4 py-2 rounded-lg border font-semibold ${watchedValues.availableDays.includes(day.value) ? BROWN + ' text-white' : 'border-neutral-300 bg-neutral-100 text-neutral-700'} focus:outline focus:ring-2 focus:ring-brown-700`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-neutral-600 mt-2">Select the days you're open; then set units below.</p>
              </div>

              {/* Daily Inventory Section */}
              {watchedValues.availableDays.length > 0 && (
                <div className="block w-full mb-4">
                  <label className="block font-semibold mb-3">Daily Inventory</label>
                  <div className="space-y-3">
                    {watchedValues.availableDays.map(dayValue => {
                      const day = daysOfWeek.find(d => d.value === dayValue);
                      const dayNames = {
                        'Mon': 'Monday',
                        'Tue': 'Tuesday', 
                        'Wed': 'Wednesday',
                        'Thu': 'Thursday',
                        'Fri': 'Friday',
                        'Sat': 'Saturday',
                        'Sun': 'Sunday'
                      };
                      return (
                        <div key={dayValue} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${BROWN} text-white`}>
                            {day.label}
                          </div>
                          <span className="font-medium text-neutral-700 min-w-[80px]">
                            {dayNames[dayValue]}
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="w-20 rounded-lg border border-neutral-300 px-3 py-2 text-center focus:outline focus:ring-2 focus:ring-brown-700"
                              value={watchedValues.dailyInventory[dayValue] || 0}
                              onChange={(e) => handleDailyInventoryChange(dayValue, e.target.value)}
                              placeholder="0"
                            />
                            <span className="text-sm text-neutral-600">units</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                                      {error && error.includes('daily inventory') && (
                      <div className="text-red-500 text-sm mt-2">{error}</div>
                    )}
                  
                  {/* Total Inventory Summary */}
                  <div className="mt-3 p-3 bg-neutral-100 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-neutral-700">Total Weekly Inventory:</span>
                      <span className="font-bold text-lg text-neutral-900">
                        {watchedValues.availableDays.reduce((total, day) => total + (watchedValues.dailyInventory[day] || 0), 0)} units
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {/* Product Status (always stacked below available days) */}
              <div className="block w-full">
                <label htmlFor="status" className="font-semibold">Product Status</label>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    id="status"
                    aria-pressed={watchedValues.status}
                    onClick={handleStatus}
                    className={`w-12 h-7 rounded-full flex items-center transition-colors duration-200 ${watchedValues.status ? 'bg-green-500' : 'bg-neutral-300'}`}
                  >
                    <span className={`block w-6 h-6 rounded-full bg-white shadow transform transition-transform duration-200 ${watchedValues.status ? 'translate-x-6' : 'translate-x-0'}`}></span>
                  </button>
                  <span className="text-sm">{watchedValues.status ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Error/Success */}
          {error && <div className="text-red-500 text-center font-semibold">{error}</div>}
          {success && <div className="text-green-600 text-center font-semibold">Product added! Redirecting…</div>}
          {/* Button group right-aligned at bottom of form, inside the card */}
          <div className="w-full flex justify-end gap-4 pt-6 border-t border-neutral-200">
            <button
              type="button"
              className="py-4 px-4 rounded-lg border-2 border-[#C97B53] bg-white text-[#C97B53] font-bold text-lg font-montserrat shadow transition hover:bg-[#F5E9E3] focus:outline focus:ring-2 focus:ring-[#C97B53] focus:ring-offset-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`py-4 px-4 rounded-lg font-bold text-lg font-montserrat shadow transition focus:outline focus:ring-2 focus:ring-[#C97B53] focus:ring-offset-2 ${loading ? 'bg-[#E0CFC2] text-white opacity-80 cursor-not-allowed' : 'bg-[#C97B53] text-white hover:bg-[#A86A46]'}`}
              disabled={loading}
            >
              {loading ? 'Adding…' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddProduct; 